import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ProposalsIcon } from '../assets';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { TableSearchInput } from '../components/inputs/TableSearchInput';
import { CreateProposal } from '../components/popups/CreateProposal';
import { ProposalSubrowTable } from '../components/proposals/ProposalSubrowsTable';
import DataTable from '../components/tables/DataTable';
import { getProposalsTableHeaders } from '../components/tables/headers/proposals';
import { replaceIds, ROUTES } from '../config/routes/config';
import { GET_PROPOSALS } from '../graphql/proposal/query';
import { useTableDataAndFilters } from '../hooks/useTableDataAndFilters';
import { proposalFilterAtom } from '../states/atoms';
import type {
  ProposalFiltersAtom,
  ProposalTable,
  ResProposalsList,
} from '../types/proposal';

function Proposals() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const {
    resetPagination,
    total,
    filters,
    setFilters,
    table,
    error,
    loading,
    refetch,
  } = useTableDataAndFilters<
    ProposalFiltersAtom,
    ProposalTable,
    {},
    ResProposalsList
  >({
    filterAtom: proposalFilterAtom,
    getColumns: getProposalsTableHeaders,
    columnsExtras: {},
    responseAccessKey: 'proposals',
    query: GET_PROPOSALS,
    subrowAccesor: 'ProposalUsers',
  });

  useEffect(() => {
    setFilters({});
    resetPagination();
  }, []);

  const handleTableClick = (id: string) => {
    navigate(
      replaceIds({
        url: ROUTES.SINGLE_PROPOSAL,
        params: [{ proposalId: id }],
      }),
    );
  };

  return (
    <div className="w-full flex flex-col gap-y-5">
      <Breadcrumbs
        icon={<ProposalsIcon />}
        breadcrumbs={[{ text: t('proposals.title') }]}
      />
      <div className="flex flex-col gap-y-4">
        <div className="flex flex-row justify-between">
          <div>
            <TableSearchInput
              placeholder={t('proposals.searchInputPlaceholder')}
              filters={{ ...filters }}
              setFilters={(val) => {
                setFilters(val);
                resetPagination();
              }}
              searchKey="name"
            />
          </div>
          <div className="flex flex-row gap-x-2">
            <CreateProposal refetch={refetch} />
          </div>
        </div>
        <DataTable
          error={error}
          handleRowClick={(id) => handleTableClick(id)}
          loading={loading}
          table={table}
          total={total}
          tableId="ProposalsTable"
          sorting={{ ...filters }}
          SubrowsComponent={ProposalSubrowTable}
        />
      </div>
    </div>
  );
}

export default Proposals;
